import { useCallback, useEffect, useRef } from 'react';
import { Map, View } from 'ol';
import 'ol/ol.css';
import { isEmpty } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { Circle as CircleGeometry } from 'ol/geom';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { Button, Col, Row, Space } from "antd";
import PermissionChecker from "../../PermissionChecker";
import http from "../../http";
import { useTranslation } from "react-i18next";
import { ArrowUpOutlined, ArrowDownOutlined, FullscreenOutlined } from '@ant-design/icons';

proj4.defs(
    'EPSG:6675',
    '+proj=tmerc +lat_0=36 +lon_0=137.1666666666667 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
);
register(proj4);

const image = new CircleStyle({
    radius: 5,
    fill: null,
    stroke: new Stroke({ color: '#DC000C', width: 1 }),
});

const styles = {
    'Point': new Style({
        image: image,
    }),
    'LineString': new Style({
        stroke: new Stroke({
            color: '#079327',
            width: 1,
        }),
    }),
    'MultiLineString': new Style({
        stroke: new Stroke({
            color: '#079327',
            width: 1,
        }),
    }),
    'MultiPoint': new Style({
        image: image,
    }),
    'MultiPolygon': new Style({
        stroke: new Stroke({
            color: 'yellow',
            width: 1,
        }),
        fill: new Fill({
            color: 'rgba(255, 255, 0, 0.1)',
        }),
    }),
    'Polygon': new Style({
        stroke: new Stroke({
            color: '#377EF3',
            lineDash: [4],
            width: 3,
        }),
        fill: new Fill({
            color: 'rgba(0, 0, 255, 0.1)',
        }),
    }),
    'GeometryCollection': new Style({
        stroke: new Stroke({
            color: 'magenta',
            width: 2,
        }),
        fill: new Fill({
            color: 'magenta',
        }),
        image: new CircleStyle({
            radius: 10,
            fill: null,
            stroke: new Stroke({
                color: 'magenta',
            }),
        }),
    }),
    'Circle': new Style({
        stroke: new Stroke({
            color: 'red',
            width: 1,
        }),
    }),
};

const styleFunction = function (feature) {
    return styles[feature.getGeometry().getType()];
};

const readDXFFeatures = (geojson) => {
    const features = new GeoJSON().readFeatures(geojson);

    features.forEach((feature) => {
        const geometry = feature.getGeometry();
        const radius = feature.get('radius');

        if (geometry && geometry.getType() === 'Point' && radius > 0) {
            feature.setGeometry(new CircleGeometry(geometry.getCoordinates(), radius));
        }
    });

    return features;
};

const vectorSource = new VectorSource();

const DXFViewer = (props) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const mapRef = useRef(null);

    const fitToDrawing = useCallback(() => {
        const extent = vectorSource.getExtent();
        if (mapRef.current && !isEmpty(extent)) {
            mapRef.current.getView().fit(extent);
        }
    }, []);

    useEffect(() => {
        const center = [0, 0];
        const zoom = 5;

        const mapObject = new Map({
            target: containerRef.current,
            layers: [
                new VectorLayer({
                    source: vectorSource,
                    style: styleFunction,
                }),
            ],
            view: new View({
                center,
                zoom,
                minZoom: 0,
                maxZoom: 28
            }),
        });

        mapRef.current = mapObject;

        return () => {
            mapObject.setTarget(null);
            mapObject.dispose();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!props.geojsonUrl) {
            vectorSource.clear();
            return undefined;
        }

        const controller = new AbortController();

        http.get(props.geojsonUrl, { signal: controller.signal })
            .then(response => {
                vectorSource.clear();
                vectorSource.addFeatures(readDXFFeatures(response.data));
                fitToDrawing();
            })
            .catch(error => {
                if (error.code !== 'ERR_CANCELED') {
                    console.log(error);
                }
            });

        return () => controller.abort();
    }, [props.geojsonUrl, fitToDrawing]);

    const uploadHandler = () => {
        if (props.handleUploadButtonClick) {
            props.handleUploadButtonClick()
        }
    }

    const downloadHandler = () => {
        http.get(props.downloadURL).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', props.downloadFileName || 'file.dxf');
            document.body.appendChild(link);
            link.click();
        }).catch((err) => {
            console.log(err)
        });

    };

    return (
        <>
            <Row style={{ justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                <Col style={{ fontWeight: "bold", fontSize: 15 }}>
                    {props.title}
                </Col>
                <Col>
                    <Space>
                        {props.downloadURL ? <PermissionChecker requiredPermissions={props.requiredPermission || []}>
                            <Button
                                icon={<ArrowDownOutlined />}
                                className={'bordered-default-button'}
                                onClick={() => downloadHandler()}
                            >
                                {t('download_button_text')}
                            </Button>
                        </PermissionChecker> : <></>}
                        {!props.hideUploadButton ? <PermissionChecker requiredPermissions={props.requiredPermission || []}>
                            <Button
                                icon={<ArrowUpOutlined />}
                                type={"primary"}
                                style={{ border: "none" }}
                                onClick={() => uploadHandler()}>
                                {t('upload_button_text')}
                            </Button>
                        </PermissionChecker> : null}
                    </Space>
                </Col>
            </Row>
            <Row style={{ height: '100%' }}>
                <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                    <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
                    <div style={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
                        <Button
                            type="button"
                            icon={<FullscreenOutlined />}
                            onClick={() => fitToDrawing()}
                        />
                    </div>
                </div>
            </Row>
        </>
    )
};

export default DXFViewer;